import {
  Activity,
  BadgeCheck,
  Banknote,
  CalendarClock,
  ClipboardList,
  Gauge,
  Receipt,
  ShieldCheck,
  UserPlus,
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { DataTable, type DataTableColumn } from '@/ui/DataTable';
import { MoneyDisplay } from '@/ui/MoneyDisplay';
import { PageHeader } from '@/ui/PageHeader';
import { StatsGrid, type StatItem } from '@/ui/StatsGrid';
import { Badge } from '@/ui/base/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/ui/base/tabs';

import {
  CartoesDeAlerta,
  Grupo,
  ListaDeContagens,
  QuadroDeEstado,
  Secao,
  SeloDeCodigo,
} from './componentes';
import { formatarDecimal, formatarInteiro } from './formato';
import { GraficoDeValoresMensais, GraficoDeVeiculosPorDre } from './graficos';
import type {
  AbaCredenciamento,
  AbaFinanceira,
  AbaGestao,
  AbaOperacional,
  Indicador,
  LinhaDataLake,
  LinhaDeDesempenho,
  PainelExecutivo,
} from './contrato';
import { carregarPainel } from './dados';
import { useColecao } from './useColecao';

/**
 * ONDA-CL1 — visão geral, quatro abas.
 *
 * Estrutura e dado conferidos no protótipo em navegador real; a matriz seção a
 * seção está em `docs/rastreabilidade/matriz-paridade-analises.md`. A aba
 * escolhida vive na URL (`?aba=`) para o endereço ser compartilhável, como nas
 * outras telas com aba deste repositório.
 *
 * Os títulos de seção são os do protótipo, ao pé da letra — é por eles que a
 * paridade se confere lado a lado, e `paridade.spec.ts` os guarda.
 */

const ABAS = ['operacional', 'financeiro', 'credenciamento', 'gestao'] as const;
type Aba = (typeof ABAS)[number];

function abaValida(valor: string | null): Aba {
  return ABAS.includes(valor as Aba) ? (valor as Aba) : 'operacional';
}

/** O painel só está vazio se nenhuma das quatro abas tiver indicador. */
function painelVazio(dado: PainelExecutivo): boolean {
  return (
    dado.operacional.indicadores.length === 0 &&
    dado.financeiro.indicadores.length === 0 &&
    dado.credenciamento.indicadores.length === 0 &&
    dado.gestao.indicadores.length === 0
  );
}

type LucideIconLike = StatItem['icon'];

/** Os indicadores vêm sem ícone do dado; o ícone é decisão de interface. */
function comIcones(
  indicadores: readonly Indicador[],
  icones: readonly LucideIconLike[],
): StatItem[] {
  return indicadores.map((indicador, i) => ({
    title: indicador.rotulo,
    value: indicador.valor,
    description: indicador.descricao,
    // O `??` não é defensivo à toa: `noUncheckedIndexedAccess` faz o índice
    // devolver `LucideIcon | undefined`, e o cartão exige ícone.
    icon: icones[i % icones.length] ?? Activity,
  }));
}

/** Linha da tabela mais o `id` que o `DataTable` exige. */
type ComId<T> = T & { id: string };

/**
 * A linha de total é a última do corpo, como no protótipo — e é por isso que
 * estas duas tabelas não ordenam: ordenar jogaria o "Total Geral" para o meio.
 * O protótipo também não oferece ordenação aqui.
 */
const ehTotal = (linha: { id: string }) =>
  linha.id === 'total' ? 'bg-muted/50 font-semibold' : undefined;

const COLUNAS_MATRIZ: DataTableColumn<ComId<LinhaDeDesempenho>>[] = [
  {
    header: 'DRE',
    cell: (l) => (
      <span>
        <SeloDeCodigo codigo={l.codigo} />
        {l.dre}
      </span>
    ),
  },
  { header: 'Comum', cell: (l) => formatarInteiro(l.comum) },
  { header: 'Acessível', cell: (l) => formatarInteiro(l.acessivel) },
  { header: 'Especial', cell: (l) => formatarInteiro(l.especial) },
  { header: 'Creche', cell: (l) => formatarInteiro(l.creche) },
  { header: 'Total', cell: (l) => formatarInteiro(l.total) },
];

/**
 * As dezesseis colunas do "Data Lake" do protótipo, todas mantidas. Cabeçalho
 * curto porque a tabela rola na horizontal dentro do próprio contêiner — a
 * página nunca rola de lado (regra 4 de interface).
 */
const COLUNAS_DATA_LAKE: DataTableColumn<ComId<LinhaDataLake>>[] = [
  {
    header: 'DRE / Tipo',
    cell: (l) => (
      <span className="whitespace-nowrap">
        <SeloDeCodigo codigo={l.codigo} />
        {l.dre}
        {l.tiposDePessoa > 0 ? (
          <Badge variant="secondary" className="ml-2">
            {l.tiposDePessoa}
          </Badge>
        ) : null}
      </span>
    ),
  },
  { header: 'Valor Bruto', cell: (l) => <MoneyDisplay value={l.valorBruto} /> },
  { header: 'INSS', cell: (l) => <MoneyDisplay value={l.inss} /> },
  { header: 'Bruto + INSS', cell: (l) => <MoneyDisplay value={l.brutoMaisInss} /> },
  { header: 'Não Cad.', cell: (l) => formatarInteiro(l.naoCadastradas) },
  { header: 'Cad.', cell: (l) => formatarInteiro(l.cadastradas) },
  { header: 'Total Cri.', cell: (l) => formatarInteiro(l.totalCriancas) },
  { header: 'KM Adic.', cell: (l) => formatarDecimal(l.kmAdicional) },
  { header: 'Conv.', cell: (l) => formatarInteiro(l.convencional) },
  { header: 'Acess.', cell: (l) => formatarInteiro(l.acessivel) },
  { header: 'Creche', cell: (l) => formatarInteiro(l.creche) },
  { header: 'Condutores', cell: (l) => formatarInteiro(l.condutores) },
  { header: 'R$/Cri./Dia', cell: (l) => <MoneyDisplay value={l.reaisPorCriancaDia} /> },
  { header: 'Rem. Méd.', cell: (l) => <MoneyDisplay value={l.remuneracaoMedia} /> },
  { header: 'Cri./Veíc.', cell: (l) => formatarDecimal(l.criancasPorVeiculo) },
  { header: 'KM/Cond.', cell: (l) => formatarDecimal(l.kmPorCondutor) },
];

function AbaOperacionalConteudo({ dado }: { dado: AbaOperacional }) {
  const vencidos = dado.alertasDeVencimento.reduce((soma, a) => soma + a.vencidos, 0);
  const proximos = dado.alertasDeVencimento.reduce((soma, a) => soma + a.proximos, 0);

  return (
    <div className="space-y-4">
      <StatsGrid
        stats={comIcones(dado.indicadores, [BadgeCheck, ClipboardList, Activity])}
        columns="grid-cols-1 sm:grid-cols-3"
      />

      <Secao titulo="Ações para hoje" descricao="Itens que exigem decisão sua no dia.">
        <ul className="space-y-2">
          {dado.acoesParaHoje.map((acao) => (
            <li
              key={acao.titulo}
              className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border p-3"
            >
              <span className="text-sm">{acao.titulo}</span>
              <Badge variant="secondary">{acao.quantidade}</Badge>
            </li>
          ))}
        </ul>
      </Secao>

      <CartoesDeAlerta itens={dado.alertasDestacados} />

      <Secao titulo="Credenciados por Status">
        <ListaDeContagens itens={dado.credenciadosPorStatus} ariaLabel="Credenciados por status" />
      </Secao>

      <Secao titulo="OS por Status">
        <ListaDeContagens itens={dado.osPorStatus} ariaLabel="Ordens de serviço por status" />
      </Secao>

      <Secao
        titulo="Alertas de Vencimento"
        descricao={`${vencidos} vencido(s) · ${proximos} próximo(s)`}
      >
        <ul className="space-y-2">
          {dado.alertasDeVencimento.map((alerta) => (
            <li
              key={alerta.documento}
              className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border p-3"
            >
              <span className="text-sm">{alerta.documento}</span>
              <span className="flex items-center gap-2">
                <Badge variant="destructive">{alerta.vencidos} vencidos</Badge>
                <Badge variant="outline">{alerta.proximos} próximos</Badge>
              </span>
            </li>
          ))}
        </ul>
      </Secao>

      <Grupo titulo="Quadro de Desempenho Operacional TEG">
        <StatsGrid
          stats={comIcones(dado.desempenhoPorTipoPessoa, [UserPlus, ShieldCheck, BadgeCheck])}
          columns="grid-cols-1 sm:grid-cols-3"
        />

        <Secao titulo="Matriz DRE × Tipo TEG" descricao="Veículos em operação por DRE.">
          <DataTable
            columns={COLUNAS_MATRIZ}
            data={[
              ...dado.desempenho.map((linha) => ({ ...linha, id: linha.codigo })),
              { ...dado.desempenhoTotal, id: 'total' },
            ]}
            getRowClassName={ehTotal}
            emptyTitle="Sem veículo no recorte"
            emptyDescription="Nenhuma diretoria com veículo em operação nesta competência."
            ariaLabel="Matriz de veículos por DRE e tipo TEG"
          />
        </Secao>

        <Secao titulo="Veículos por DRE" descricao="Distribuição por Tipo TEG.">
          <GraficoDeVeiculosPorDre dados={dado.desempenho} />
        </Secao>
      </Grupo>
    </div>
  );
}

function AbaFinanceiraConteudo({ dado }: { dado: AbaFinanceira }) {
  return (
    <div className="space-y-4">
      <StatsGrid
        stats={comIcones(dado.indicadores, [
          Banknote,
          Receipt,
          ClipboardList,
          Gauge,
          Activity,
          ShieldCheck,
        ])}
        columns="grid-cols-2 md:grid-cols-3 xl:grid-cols-4"
      />

      {/* Gráfico, não tabela: é assim que o protótipo desenha esta seção. */}
      <Secao titulo="Valores Mensais" descricao="Últimos 6 meses">
        <GraficoDeValoresMensais dados={dado.valoresMensais} />
      </Secao>

      <Secao titulo="Últimas Medições Aprovadas">
        <DataTable
          columns={[
            { header: 'Credenciado', accessorKey: 'credenciado', sortKey: 'credenciado' },
            { header: 'Mês Ref.', accessorKey: 'mesReferencia', sortKey: 'mesReferencia' },
            { header: 'Valor', cell: (l) => <MoneyDisplay value={l.valor} /> },
            { header: 'Aprovação', cell: (l) => <Badge variant="secondary">{l.aprovacao}</Badge> },
          ]}
          data={[...dado.ultimasMedicoes]}
          emptyTitle="Nenhuma medição aprovada"
          emptyDescription="Não houve aprovação registrada na competência."
          ariaLabel="Últimas medições aprovadas"
        />
      </Secao>

      <Secao titulo="Últimas Multas Aplicadas">
        <DataTable
          columns={[
            { header: 'Credenciado', accessorKey: 'credenciado', sortKey: 'credenciado' },
            { header: 'Infração', accessorKey: 'infracao' },
            { header: 'Valor', cell: (l) => <MoneyDisplay value={l.valor} /> },
            { header: 'Status', cell: (l) => <Badge variant="outline">{l.status}</Badge> },
          ]}
          data={[...dado.ultimasMultas]}
          emptyTitle="Nenhuma multa aplicada"
          emptyDescription="Não houve penalidade registrada na competência."
          ariaLabel="Últimas multas aplicadas"
        />
      </Secao>

      <Secao
        titulo="Data Lake — Indicadores por DRE × Tipo de Pessoa"
        descricao="Visão consolidada operacional e financeira"
      >
        <DataTable
          columns={COLUNAS_DATA_LAKE}
          data={[
            ...dado.dataLake.map((linha) => ({ ...linha, id: linha.codigo })),
            { ...dado.dataLakeTotal, id: 'total' },
          ]}
          getRowClassName={ehTotal}
          emptyTitle="Sem indicadores consolidados"
          emptyDescription="Nenhuma competência consolidada para este recorte."
          ariaLabel="Indicadores por DRE e tipo de pessoa"
        />
      </Secao>
    </div>
  );
}

function AbaCredenciamentoConteudo({ dado }: { dado: AbaCredenciamento }) {
  return (
    <div className="space-y-4">
      <StatsGrid
        stats={comIcones(dado.indicadores, [UserPlus, Activity, BadgeCheck, ShieldCheck])}
      />

      <Secao titulo="Inscrições por Status">
        <ListaDeContagens itens={dado.inscricoesPorStatus} ariaLabel="Inscrições por status" />
      </Secao>

      <Secao titulo="Últimas Inscrições Analisadas">
        <DataTable
          columns={[
            { header: 'Protocolo', accessorKey: 'protocolo', sortKey: 'protocolo' },
            { header: 'Nome', accessorKey: 'nome', sortKey: 'nome' },
            { header: 'Status', cell: (l) => <Badge variant="secondary">{l.status}</Badge> },
            { header: 'Data', accessorKey: 'data', sortKey: 'data' },
          ]}
          data={[...dado.ultimasInscricoes]}
          emptyTitle="Nenhuma inscrição analisada"
          emptyDescription="Não houve decisão registrada no período."
          ariaLabel="Últimas inscrições analisadas"
        />
      </Secao>
    </div>
  );
}

function AbaGestaoConteudo({ dado }: { dado: AbaGestao }) {
  return (
    <div className="space-y-4">
      <StatsGrid
        stats={comIcones(dado.indicadores, [Activity, ShieldCheck, Gauge, ClipboardList])}
      />

      <Secao titulo="OS por Status">
        <ListaDeContagens itens={dado.osPorStatus} ariaLabel="Ordens de serviço por status" />
      </Secao>

      <Secao titulo="Últimas Ações de Auditoria">
        <DataTable
          columns={[
            { header: 'Ação', accessorKey: 'acao' },
            { header: 'Tabela', accessorKey: 'tabela', sortKey: 'tabela' },
            { header: 'Data', accessorKey: 'data', sortKey: 'data' },
          ]}
          data={[...dado.ultimasAuditorias]}
          emptyTitle="Nenhuma ação registrada"
          emptyDescription="A trilha de auditoria não tem lançamento no período."
          ariaLabel="Últimas ações de auditoria"
        />
      </Secao>
    </div>
  );
}

export function VisaoGeralPage() {
  const [parametros, definirParametros] = useSearchParams();
  const aba = abaValida(parametros.get('aba'));
  const { estado, recarregar } = useColecao(carregarPainel, painelVazio);

  function mudarAba(nova: string) {
    definirParametros((atual) => {
      const proximo = new URLSearchParams(atual);
      proximo.set('aba', nova);
      return proximo;
    });
  }

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Análises e notificações"
        title="Visão Geral"
        description="Resumo do Transporte Escolar Gratuito"
      />

      <QuadroDeEstado
        estado={estado}
        recarregar={recarregar}
        tituloVazio="Sem dados para a visão geral"
        descricaoVazio="Nenhuma competência consolidada ainda. O painel se preenche quando houver movimento registrado."
      >
        {(dado) => (
          <Tabs value={aba} onValueChange={mudarAba}>
            <TabsList>
              <TabsTrigger value="operacional">
                <Activity className="h-4 w-4" aria-hidden="true" />
                Operacional
              </TabsTrigger>
              <TabsTrigger value="financeiro">
                <Banknote className="h-4 w-4" aria-hidden="true" />
                Financeiro
              </TabsTrigger>
              <TabsTrigger value="credenciamento">
                <UserPlus className="h-4 w-4" aria-hidden="true" />
                Credenciamento
              </TabsTrigger>
              <TabsTrigger value="gestao">
                <CalendarClock className="h-4 w-4" aria-hidden="true" />
                Gestão
              </TabsTrigger>
            </TabsList>

            <TabsContent value="operacional" className="space-y-4">
              <AbaOperacionalConteudo dado={dado.operacional} />
            </TabsContent>
            <TabsContent value="financeiro" className="space-y-4">
              <AbaFinanceiraConteudo dado={dado.financeiro} />
            </TabsContent>
            <TabsContent value="credenciamento" className="space-y-4">
              <AbaCredenciamentoConteudo dado={dado.credenciamento} />
            </TabsContent>
            <TabsContent value="gestao" className="space-y-4">
              <AbaGestaoConteudo dado={dado.gestao} />
            </TabsContent>
          </Tabs>
        )}
      </QuadroDeEstado>
    </div>
  );
}

export default VisaoGeralPage;
